import React from 'react';
import { motion } from 'framer-motion';
import { FaEye, FaFileAlt, FaBullseye } from 'react-icons/fa';

interface AboutVisionMissionGoalsSectionProps {
  language?: 'ar' | 'en';
  visionAr?: string;
  visionEn?: string;
  missionAr?: string;
  missionEn?: string;
  goalsAr?: string;
  goalsEn?: string;
  visionIconColor?: string;
  missionIconColor?: string;
  goalsIconColor?: string;
  visionBgColor?: string;
  missionBgColor?: string;
  goalsBgColor?: string;
  sectionBgColor?: string;
}

const AboutVisionMissionGoalsSection: React.FC<AboutVisionMissionGoalsSectionProps> = ({
  language = 'ar',
  visionAr = "أن تكون مكة البركة اسمًا رائدًا في مجال الطرق والأعمال الترابية والمحاجر في مصر والمنطقة.",
  visionEn = "To be Mecca El Baraka a leading name in the field of roads, earthworks and quarries in Egypt and the region.",
  missionAr = "نشر ثقافة الجودة وتقديم خدمات المقاولات بمعايير عالمية مع أفضل تكلفة وجودة.",
  missionEn = "Spreading a culture of quality and providing contracting services with international standards with the best cost and quality.",
  goalsAr = "الارتقاء بجودة جميع أعمالنا وتحقيق رضا العملاء وتقديم أعلى مستويات السلامة والاحترافية.",
  goalsEn = "Elevating the quality of all our work, achieving customer satisfaction and providing the highest levels of safety and professionalism.",
  visionIconColor = "#3b82f6",
  missionIconColor = "#10b981",
  goalsIconColor = "#8b5cf6",
  visionBgColor = "#3b82f6",
  missionBgColor = "#10b981",
  goalsBgColor = "#8b5cf6",
  sectionBgColor = "from-gray-50 to-blue-50"
}) => {
  const isRTL = language === 'ar';

  return (
    <section className={`section-padding bg-gradient-to-br ${sectionBgColor}`}>
      <div className="container-custom">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-900" data-editable="text">{isRTL ? 'الرؤية والرسالة والأهداف' : 'Vision, Mission & Goals'}</h2>
        <div className="grid md:grid-cols-3 gap-8">
          {/* Vision */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl shadow-lg p-8 text-center"
            style={{ backgroundColor: visionBgColor !== "#3b82f6" ? visionBgColor : undefined }}
          >
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: visionBgColor }}
              data-editable="icon"
            >
              <FaEye className="w-8 h-8 text-white" style={{ color: visionIconColor }} />
            </div>
            <h3 className="text-xl font-bold mb-4 text-gray-900" data-editable="text">{isRTL ? 'الرؤية' : 'Vision'}</h3>
            <p className="text-gray-700" data-editable="text">
              {isRTL ? visionAr : visionEn}
            </p>
          </motion.div>

          {/* Mission */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl shadow-lg p-8 text-center"
            style={{ backgroundColor: missionBgColor !== "#10b981" ? missionBgColor : undefined }}
          >
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: missionBgColor }}
              data-editable="icon"
            >
              <FaFileAlt className="w-8 h-8 text-white" style={{ color: missionIconColor }} />
            </div>
            <h3 className="text-xl font-bold mb-4 text-gray-900" data-editable="text">{isRTL ? 'الرسالة' : 'Mission'}</h3>
            <p className="text-gray-700" data-editable="text">
              {isRTL ? missionAr : missionEn}
            </p>
          </motion.div>

          {/* Goals */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="bg-white rounded-xl shadow-lg p-8 text-center"
            style={{ backgroundColor: goalsBgColor !== "#8b5cf6" ? goalsBgColor : undefined }}
          >
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: goalsBgColor }}
              data-editable="icon"
            >
              <FaBullseye className="w-8 h-8 text-white" style={{ color: goalsIconColor }} />
            </div>
            <h3 className="text-xl font-bold mb-4 text-gray-900" data-editable="text">{isRTL ? 'الأهداف' : 'Goals'}</h3>
            <p className="text-gray-700" data-editable="text">
              {isRTL ? goalsAr : goalsEn}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutVisionMissionGoalsSection; 